import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type EscalateCaseBreachesResult,
  type GetCaseQueueResult,
} from '@whinerp/client';
import { DataTable, EmptyState, ErrorState, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { slaChip } from './sla.js';
import { NewCasePanel } from './new-case-panel.js';
import { SurveyPanel } from './survey-panel.js';
import { DispatchPanel } from './dispatch-panel.js';
import { ComplaintsRegisterPanel } from './complaints-panel.js';
import { defineScreen } from '../types.js';

/**
 * SC-28 Cases.
 *
 * Sorted by SLA urgency, from the server. A queue sorted newest-first buries
 * the ticket that has been waiting since Tuesday under the ones that arrived
 * this morning - and the one waiting since Tuesday is the one about to breach.
 *
 * The SLA column is a colour AND a word. A row that says "overdue" only by
 * being red says nothing in a printed handover sheet and nothing to an agent
 * who cannot see red.
 *
 * Opening this screen is what escalates (F-087). There is no scheduler in this
 * product, so the alternative to running the sweep here was a breach that
 * stayed a dashboard entry - which is what it was, and why the claim had been
 * withdrawn. It is safe to fire on every open because each breach is claimed
 * once in the database: the second agent to open the queue escalates nothing.
 *
 * The banner says how many people were told, not only how many cases breached.
 * "3 cases breached" invites the manager to assume somebody was told, and the
 * defect this closes is precisely that nobody was.
 */

type Queue = GetCaseQueueResult;
type Tab = 'unassigned' | 'mine' | 'all';

function CasesListScreen(): JSX.Element {
  const t = useT();
  const { session } = useSession();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('unassigned');

  const queue = useQuery<Queue, ApiError>({
    queryKey: ['case-queue'],
    queryFn: () => api.getCaseQueue({}),
  });

  /*
   * The sweep, once per mount.
   *
   * A ref rather than a query, because this writes: React 18's development
   * double-mount would otherwise fire it twice, and while the server would
   * claim nothing the second time, a write fired twice by a rendering
   * strategy is a habit that stops being harmless the moment it is copied to
   * something not idempotent.
   *
   * Failure is deliberately quiet. An agent opening the queue came to work
   * cases; an error banner about a background sweep would sit on top of the
   * work, and the queue itself is unaffected by it.
   */
  const swept = useRef(false);
  const escalate = useMutation<EscalateCaseBreachesResult, ApiError>({
    mutationFn: () => api.escalateCaseBreaches({}),
    onSuccess: (result) => {
      // The bell is on the same screen. Without this it shows yesterday's
      // count beside a banner announcing today's.
      if (result.escalated > 0) void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  const fire = escalate.mutate;
  useEffect(() => {
    if (swept.current) return;
    swept.current = true;
    fire();
  }, [fire]);

  if (queue.isPending) return <CasesLoading />;
  if (queue.isError) return <CasesError error={queue.error} retry={() => void queue.refetch()} />;

  const lists: Record<Tab, Queue['all']> = {
    unassigned: queue.data.unassigned,
    mine: queue.data.mine,
    all: queue.data.all,
  };
  const rows = lists[tab];

  return (
    <div data-component="CasesList" className="flex w-full flex-col gap-3">
      <header className="flex flex-wrap items-center gap-3">
        <SectionLabel>{t('case.title')}</SectionLabel>
        <span role="group" aria-label={t('case.title')} className="flex gap-1">
          {(['unassigned', 'mine', 'all'] as const).map((option) => (
            <button
              key={option}
              type="button"
              data-queue-tab={option}
              aria-pressed={tab === option}
              onClick={() => setTab(option)}
              className={
                tab === option
                  ? 'inline-flex h-touch items-center gap-1 rounded-control bg-bronze-tint px-3 text-dense text-bronze-text'
                  : 'inline-flex h-touch items-center gap-1 rounded-control px-3 text-dense text-ink-600 hover:bg-sunken'
              }
            >
              {t(`case.queue.${option}` as 'case.queue.mine')}
              {/* The count comes from the same list underneath it. */}
              <Num data-queue-count={option} className="text-meta">
                {formatCount(lists[option].length, format)}
              </Num>
            </button>
          ))}
        </span>

        {/*
          * The other view of the same records, one click away.
          *
          * This queue is what is still to be worked, so it leaves closed cases
          * out and does not page. "What did we close last month" is a real
          * question and not one a queue can answer - SC-58 is where it goes.
          */}
        <Link
          data-cases-audit
          to="/cases/list"
          className="ms-auto text-dense text-bronze-text hover:text-ink"
        >
          {t('list.cases.showAsList')}
        </Link>
      </header>

      {/* A case taken on the phone or in person (G-193); the other channels make their own. */}
      <NewCasePanel />
      {/* How customers rated what was resolved (G-197). */}
      <SurveyPanel />
      {/* Who goes where today (G-201). */}
      <DispatchPanel />
      {/* Formal complaints and their deadlines (G-203). */}
      <ComplaintsRegisterPanel />

      {/*
        * Only when this opening found something.
        *
        * Several agents open this queue several times an hour, and a banner
        * that reappeared each time would be read as a new breach each time -
        * which is how a warning becomes furniture.
        */}
      {escalate.data && escalate.data.escalated > 0 ? (
        <EscalationBanner result={escalate.data} />
      ) : null}

      {rows.length === 0 ? (
        <CasesEmpty reason={tab === 'all' ? 'no-records' : 'filtered-to-nothing'} />
      ) : (
        <Panel>
          <DataTable
            mode={session.preferences.density}
            data-cases-table
            label={t('case.title')}
            rows={rows}
            rowId={(row) => row.caseId}
            rowHref={(row) => `/cases/${row.caseId}`}
            columns={[
              { key: 'number', header: t('case.number'), essential: true, cell: (row) => row.number },
              { key: 'subject', header: t('case.subject'), cell: (row) => row.subject },
              {
                // Where it came in - one queue for every channel (G-193).
                key: 'source',
                header: t('case.cameIn'),
                cell: (row) => <span data-case-source={row.source} className="text-meta text-ink-meta">{t(`case.source.${row.source as 'phone'}`)}</span>,
              },
              {
                key: 'contact',
                header: t('case.contact'),
                cell: (row) => row.contactName ?? '',
              },
              {
                key: 'priority',
                header: t('case.priority'),
                /* The word, not a colour. A priority shown only as a tint is
                   invisible on a printed rota. */
                cell: (row) => t(`case.priority.${row.priority}` as 'case.priority.normal'),
              },
              {
                // Kept in Simple: F-087 is that a breached case says overdue in
                // words rather than only in colour, and this is the column that
                // says it.
                essential: true,
                key: 'sla',
                header: t('case.sla'),
                cell: (row) => {
                  const chip = slaChip(row.sla, format, t);
                  return (
                    <span
                      data-sla={row.caseId}
                      data-sla-urgency={chip.urgency}
                      className={
                        chip.tone === 'danger'
                          ? 'text-dense text-danger-text'
                          : chip.tone === 'warning'
                            ? 'text-dense text-warning-text'
                            : chip.tone === 'success'
                              ? 'text-dense text-success-text'
                              : 'text-dense text-ink-meta'
                      }
                    >
                      {chip.label}
                    </span>
                  );
                },
              },
              {
                key: 'assignee',
                header: t('case.assignee'),
                cell: (row) => row.assignedToName ?? t('case.unassigned'),
              },
            ]}
            labels={{
              sortBy: (column) => t('list.sortBy', { values: { column } }),
              selectRow: (row) => t('list.selectRow', { values: { row } }),
              rowsShown: (count) => t('list.rowsShown', { values: { count: formatCount(count, format) } }),
            }}
          />
        </Panel>
      )}
    </div>
  );
}

/**
 * What the sweep just did.
 *
 * Three sentences, not one with a number substituted, because the third case
 * is a different fact: breaches that reached nobody. That happens when the
 * assignee is deactivated and unreplaced, or when everybody has muted the
 * kind, and reporting it as "0 people have been told" inside a success-shaped
 * banner would be a label that lies.
 */
function EscalationBanner({ result }: { result: EscalateCaseBreachesResult }): JSX.Element {
  const t = useT();
  const format = useFormat();
  const told = result.told.reduce((sum, entry) => sum + entry.toldCount, 0);
  const values = {
    cases: formatCount(result.escalated, format),
    people: formatCount(told, format),
  };

  return (
    <p
      data-escalation={result.escalated}
      data-escalation-told={told}
      role="status"
      className={
        told === 0
          ? 'rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text'
          : 'rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text'
      }
    >
      {told === 0
        ? t('case.escalated.nobody', { values })
        : result.escalated === 1
          ? t('case.escalated.one', { values })
          : t('case.escalated', { values })}
    </p>
  );
}

function CasesEmpty({
  reason,
}: {
  reason: 'no-records' | 'filtered-to-nothing' | 'permission-denied';
}): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason={reason}
      illustration="Lifebuoy"
      title={t('case.empty.title')}
      body={t('case.empty.body')}
      primary={{ label: t('empty.action.newCase'), icon: 'Plus', href: '/cases' }}
    />
  );
}

function CasesLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-2" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      {Array.from({ length: 8 }, (_, row) => (
        <Skeleton key={row} className="h-row-comfortable w-full" />
      ))}
    </div>
  );
}

function CasesError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="The queue could not be loaded"
      body={error instanceof Error ? error.message : 'The case list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-28',
  route: '/cases',
  surface: 'page',
  title: 'Cases',
  /*
   * F-087 is back, and both halves of it are real.
   *
   * The policies were always here - deadlines stamped at creation, the queue
   * ordered by urgency, the chip saying breached in words. The escalation was
   * not, so the claim was withdrawn and a tripwire held the place: a support
   * manager who read the notification preferences believed they would be told
   * and would not have been. Opening this screen now tells them, and the
   * banner says how many people it reached.
   */
  features: ['F-085', 'F-087'],
  entitlement: 'F-085',
  reachedBy: [{ kind: 'nav', item: 'support' }],
  components: ['DataTable'],
  elements: ['CasesList'],
  states: { empty: CasesEmpty, loading: CasesLoading, error: CasesError },
  component: CasesListScreen,
});

export default screen;
